import { Paper, Typography, TextField, Box, Checkbox, MenuItem, Select, Autocomplete } from "@mui/material";
import skillsData from '../data/skills.json'
import qualitiesData from '../data/qualities.json'
import { RollModEditor, ItemChangesEditor, SkillCharSwapEditor, ChoicesEditor, VehicleChangesEditor } from './ModEditors'

const DERIVED_ATTRS = [
    { key: "SoakValue", label: "Soak" },
    { key: "DefenseMelee", label: "Melee Defense" },
    { key: "DefenseRanged", label: "Ranged Defense" },
    { key: "WoundThreshold", label: "Wound Threshold" },
    { key: "StrainThreshold", label: "Strain Threshold" },
    { key: "ForceRating", label: "Force Rating" },
    { key: "CyberCapacity", label: "Cyber Capacity" },
];

const TalentEdit = ({ editedItem, index, appFiles, setAppFiles }) => {

    const skills = appFiles.skills ? JSON.parse(appFiles.skills) : skillsData;
    const qualities = appFiles.qualities ? JSON.parse(appFiles.qualities) : qualitiesData;

    const handleEdit = (newItem) => {
        let newFiles = { ...appFiles };
        let newCat = JSON.parse(appFiles["talents"]);
        newCat[index] = newItem;
        newFiles["talents"] = JSON.stringify(newCat);
        setAppFiles(newFiles);
        localStorage.setItem('appFiles', JSON.stringify(newFiles));
    };

    // Career skills granted by this talent: ChooseCareerSkills.NewSkills.Key,
    // which may be a single key or an array. Normalize to an array for editing.
    const rawCareerKeys = editedItem?.ChooseCareerSkills?.NewSkills?.Key;
    const careerSkillKeys = rawCareerKeys
        ? (Array.isArray(rawCareerKeys) ? rawCareerKeys : [rawCareerKeys])
        : [];

    const setCareerSkills = (keys) => {
        const next = { ...editedItem };
        const cleaned = keys.filter(Boolean);
        if (cleaned.length === 0) {
            // Drop NewSkills; drop ChooseCareerSkills entirely if nothing else lives on it.
            if (next.ChooseCareerSkills) {
                const { NewSkills, ...restChoose } = next.ChooseCareerSkills;
                if (Object.keys(restChoose).length === 0) { delete next.ChooseCareerSkills; }
                else { next.ChooseCareerSkills = restChoose; }
            }
        } else {
            next.ChooseCareerSkills = {
                ...(next.ChooseCareerSkills || {}),
                NewSkills: { Key: cleaned },
            };
        }
        handleEdit(next);
    };

    // Roll modifiers: DieModifiers may be a single object or an array. Normalize.
    const dieModifiers = Array.isArray(editedItem?.DieModifiers)
        ? editedItem.DieModifiers
        : (editedItem?.DieModifiers ? [editedItem.DieModifiers] : []);

    const setDieModifiers = (mods) => {
        const next = { ...editedItem };
        if (!mods || mods.length === 0) { delete next.DieModifiers; }
        else { next.DieModifiers = mods; }
        handleEdit(next);
    };

    const itemChanges = Array.isArray(editedItem?.ItemChanges)
        ? editedItem.ItemChanges
        : (editedItem?.ItemChanges ? [editedItem.ItemChanges] : []);

    const setItemChanges = (changes) => {
        const next = { ...editedItem };
        if (!changes || changes.length === 0) { delete next.ItemChanges; }
        else { next.ItemChanges = changes; }
        handleEdit(next);
    };

    const setSkillChars = (skillChar) => {
        const next = { ...editedItem };
        if (!skillChar) { delete next.SkillChars; }
        else { next.SkillChars = { SkillChar: skillChar }; }
        handleEdit(next);
    };

    const chooseCareer = editedItem?.ChooseCareerSkills || {};

    const setSkillCount = (value) => {
        const next = { ...editedItem };
        const cc = { ...(next.ChooseCareerSkills || {}) };
        const n = parseInt(value);
        if (!value || isNaN(n) || n <= 0) {
            delete cc.SkillCount;
            delete cc.Type;
        } else {
            cc.SkillCount = value;
            if (!cc.Type) { cc.Type = "NonCareer"; }
        }
        if (Object.keys(cc).length === 0) { delete next.ChooseCareerSkills; }
        else { next.ChooseCareerSkills = cc; }
        handleEdit(next);
    };

    const setChooseType = (value) => {
        const next = { ...editedItem };
        const cc = { ...(next.ChooseCareerSkills || {}) };
        cc.Type = value;
        next.ChooseCareerSkills = cc;
        handleEdit(next);
    };

    const skillTypes = [...new Set(skills.map((s) => s.type).filter(Boolean))];

    const choices = Array.isArray(editedItem?.Choices) ? editedItem.Choices : [];
    const setChoices = (newChoices) => {
        const next = { ...editedItem };
        if (!newChoices || newChoices.length === 0) { delete next.Choices; }
        else { next.Choices = newChoices; }
        handleEdit(next);
    };

    const setVehicleChanges = (vc) => {
        const next = { ...editedItem };
        if (!vc) { delete next.VehicleChanges; }
        else { next.VehicleChanges = vc; }
        handleEdit(next);
    };

    const setDerivedAttr = (attrKey, value) => {
        const next = { ...editedItem };
        const attrs = { ...(next.Attributes || {}) };
        if (value === "" || value === "0" || parseInt(value) === 0 || isNaN(parseInt(value))) {
            delete attrs[attrKey];
        } else {
            attrs[attrKey] = value;
        }
        // Preserve Requirement / other keys; drop Attributes only if now empty.
        if (Object.keys(attrs).length === 0) { delete next.Attributes; }
        else { next.Attributes = attrs; }
        handleEdit(next);
    };

    const requirement = editedItem?.Attributes?.Requirement || {};

    const setRequirement = (patch) => {
        const next = { ...editedItem };
        const attrs = { ...(next.Attributes || {}) };
        const req = { ...(attrs.Requirement || {}) };
        Object.entries(patch).forEach(([k, v]) => {
            if (v === undefined || v === "" || v === "false") { delete req[k]; }
            else { req[k] = v; }
        });
        if (Object.keys(req).length === 0) { delete attrs.Requirement; }
        else { attrs.Requirement = req; }
        if (Object.keys(attrs).length === 0) { delete next.Attributes; }
        else { next.Attributes = attrs; }
        handleEdit(next);
    };

    const sortedSkills = [...skills].sort((a, b) => (a.skill || "").localeCompare(b.skill || ""));

    return (
        <Paper sx={{ padding: "10px" }}>
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "20px" }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography>Force Talent</Typography>
                    <Checkbox
                        checked={editedItem?.ForceTalent === "true"}
                        onChange={(e) =>
                            handleEdit({ ...editedItem, ForceTalent: e.target.checked ? "true" : "false" })
                        }
                    />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography>Conflict</Typography>
                    <Checkbox
                        checked={editedItem?.Conflict === "1"}
                        onChange={(e) =>
                            handleEdit({ ...editedItem, Conflict: e.target.checked ? "1" : undefined })
                        }
                    />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography>Ranked</Typography>
                    <Checkbox
                        checked={editedItem?.Ranked === "true"}
                        onChange={(e) => {
                            const next = { ...editedItem };
                            if (e.target.checked) { next.Ranked = "true"; }
                            else { delete next.Ranked; }
                            handleEdit(next);
                        }}
                    />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography>Halve Mod Cost</Typography>
                    <Checkbox
                        checked={editedItem?.Attributes?.HalveModCost === "true"}
                        onChange={(e) => {
                            const next = { ...editedItem };
                            const attrs = { ...(next.Attributes || {}) };
                            if (e.target.checked) { attrs.HalveModCost = "true"; }
                            else { delete attrs.HalveModCost; }
                            if (Object.keys(attrs).length === 0) { delete next.Attributes; }
                            else { next.Attributes = attrs; }
                            handleEdit(next);
                        }}
                    />
                </Box>
            </Box>

            <Autocomplete
                sx={{ marginTop: "10px", width: "50%" }}
                renderInput={(params) => <TextField {...params} label="Activation" />}
                value={editedItem?.ActivationValue || "Passive"}
                options={["Passive", "Active"]}
                onChange={(event, newValue) =>
                    handleEdit({ ...editedItem, ActivationValue: newValue })
                }
            />

            <Typography variant="h6" sx={{ marginTop: "15px" }}>Career Skills Granted:</Typography>
            <Select
                multiple
                value={careerSkillKeys}
                onChange={(e) => setCareerSkills(e.target.value)}
                displayEmpty
                renderValue={(sel) =>
                    sel.length === 0
                        ? "None"
                        : sel.map((k) => skills.find((s) => s.Key === k)?.skill || k).join(", ")
                }
                sx={{ minWidth: "300px", maxWidth: "100%" }}
            >
                {sortedSkills.map((skill) => (
                    <MenuItem key={skill.Key} value={skill.Key}>{skill.skill}</MenuItem>
                ))}
            </Select>

            <Typography variant="h6" sx={{ marginTop: "15px" }}>Player-Chosen Career Skills:</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "15px", flexWrap: "wrap", marginTop: "5px" }}>
                <TextField
                    label="Number to Choose"
                    value={chooseCareer.SkillCount || ""}
                    onChange={(e) => { if (!isNaN(e.target.value)) { setSkillCount(e.target.value); } }}
                    sx={{ maxWidth: "160px" }}
                />
                {chooseCareer.SkillCount && (
                    <Select
                        value={chooseCareer.Type || "NonCareer"}
                        onChange={(e) => setChooseType(e.target.value)}
                        sx={{ minWidth: "180px" }}
                    >
                        <MenuItem value="NonCareer">Any Skill</MenuItem>
                        {skillTypes.map((tp) => (
                            <MenuItem key={tp} value={tp}>{`${tp} Only`}</MenuItem>
                        ))}
                    </Select>
                )}
            </Box>

            <Typography variant="h6" sx={{ marginTop: "15px" }}>Roll Modifiers:</Typography>
            <RollModEditor
                dieModifiers={dieModifiers}
                skills={skills}
                onChange={setDieModifiers}
            />

            <Typography variant="h6" sx={{ marginTop: "15px" }}>Derived Attribute Bonuses:</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px", marginTop: "5px" }}>
                {DERIVED_ATTRS.map((attr) => (
                    <TextField
                        key={attr.key}
                        label={attr.label}
                        value={editedItem?.Attributes?.[attr.key] || 0}
                        onChange={(e) => {
                            if (!isNaN(e.target.value)) { setDerivedAttr(attr.key, e.target.value); }
                        }}
                        sx={{ maxWidth: "140px" }}
                    />
                ))}
            </Box>

            <Typography variant="subtitle2" sx={{ marginTop: "10px" }}>Only Apply Bonuses When:</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap", marginTop: "5px" }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography>Wearing Armor</Typography>
                    <Checkbox
                        checked={requirement.WearingArmor === "true"}
                        onChange={(e) => setRequirement({ WearingArmor: e.target.checked ? "true" : undefined })}
                    />
                </Box>
                <TextField
                    label="Armor Soak At Least"
                    value={requirement.SoakAtLeast || ""}
                    onChange={(e) => {
                        if (!isNaN(e.target.value)) { setRequirement({ SoakAtLeast: e.target.value }); }
                    }}
                    sx={{ maxWidth: "160px" }}
                />
            </Box>

            <Typography variant="h6" sx={{ marginTop: "15px" }}>Alternate Characteristics:</Typography>
            <SkillCharSwapEditor
                value={editedItem?.SkillChars?.SkillChar}
                skills={skills}
                onChange={setSkillChars}
            />

            <Typography variant="h6" sx={{ marginTop: "15px" }}>Player Choices:</Typography>
            <ChoicesEditor
                choices={choices}
                skills={skills}
                onChange={setChoices}
            />

            <Typography variant="h6" sx={{ marginTop: "15px" }}>Signature Vehicle Effects:</Typography>
            <VehicleChangesEditor
                value={editedItem?.VehicleChanges || {}}
                qualities={qualities}
                onChange={setVehicleChanges}
            />

            <Typography variant="h6" sx={{ marginTop: "15px" }}>Weapon / Attack Buffs:</Typography>
            <ItemChangesEditor
                itemChanges={itemChanges}
                skills={skills}
                qualities={qualities}
                onChange={setItemChanges}
            />
        </Paper>
    )
}

export default TalentEdit
